import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';

const S = {
  page: { padding: '24px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  subtitle: { fontSize: 14, color: '#8B99AE' },
  tabs: { display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #1E293B', paddingBottom: 0 },
  tab: (active) => ({
    padding: '10px 18px', fontSize: 13, fontWeight: active ? 600 : 400,
    color: active ? '#3B82F6' : '#64748B', background: 'none', border: 'none',
    borderBottom: active ? '2px solid #3B82F6' : '2px solid transparent',
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
  }),
  card: { background: '#111827', border: '1px solid #1E293B', borderRadius: 10, padding: 20, marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #1E293B', color: '#64748B', fontWeight: 600, fontSize: 12 },
  td: { padding: '10px 12px', borderBottom: '1px solid rgba(30,41,59,0.5)' },
  pos: (p) => ({
    fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 14, textAlign: 'center',
    color: !p ? '#4B5C73' : p <= 3 ? '#22C55E' : p <= 10 ? '#3B82F6' : p <= 20 ? '#F59E0B' : '#EF4444',
    background: !p ? 'transparent' : p <= 3 ? 'rgba(34,197,94,0.08)' : p <= 10 ? 'rgba(59,130,246,0.08)' : 'transparent',
  }),
  btn: (variant = 'primary', disabled = false) => ({
    padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
    opacity: disabled ? 0.5 : 1, transition: 'all 0.2s',
    ...(variant === 'primary' ? { background: '#3B82F6', color: '#fff' } :
      variant === 'secondary' ? { background: '#1E293B', color: '#94A3B8', border: '1px solid #2D3A4F' } :
      { background: 'rgba(34,197,94,0.12)', color: '#22C55E' }),
  }),
  badge: (type) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
    ...(type === 'high' ? { background: 'rgba(239,68,68,0.12)', color: '#EF4444' } :
      type === 'medium' ? { background: 'rgba(245,158,11,0.12)', color: '#F59E0B' } :
      type === 'low' ? { background: 'rgba(34,197,94,0.12)', color: '#22C55E' } :
      { background: 'rgba(59,130,246,0.12)', color: '#3B82F6' }),
  }),
  stat: { display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 },
  statBox: { background: '#1A2237', borderRadius: 8, padding: '12px 16px', minWidth: 120, flex: 1 },
  statLabel: { fontSize: 11, color: '#64748B', marginBottom: 4 },
  statValue: { fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" },
  progress: { height: 6, background: '#1E293B', borderRadius: 3, overflow: 'hidden', marginTop: 4 },
  progressFill: (pct, color) => ({ height: '100%', width: pct + '%', background: color || '#3B82F6', borderRadius: 3, transition: 'width 0.5s' }),
  gap: { display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(30,41,59,0.3)' },
  gapBar: (ours, theirs) => {
    const max = Math.max(ours || 0, theirs || 0, 1);
    return { height: 8, borderRadius: 4, flex: 1 };
  },
  alert: (type) => ({
    padding: '12px 16px', borderRadius: 8, marginBottom: 12, fontSize: 13,
    border: '1px solid',
    ...(type === 'warning' ? { background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.2)', color: '#F59E0B' } :
      type === 'success' ? { background: 'rgba(34,197,94,0.08)', borderColor: 'rgba(34,197,94,0.2)', color: '#22C55E' } :
      { background: 'rgba(59,130,246,0.08)', borderColor: 'rgba(59,130,246,0.2)', color: '#94A3B8' }),
  }),
};

export default function SiteCompetitors() {
  const { siteId } = useParams();
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(null); // keyword_id being analyzed
  const [selectedKw, setSelectedKw] = useState(null); // selected keyword detail
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [gapLoading, setGapLoading] = useState(false);
  const [error, setError] = useState('');
  const [customKeyword, setCustomKeyword] = useState('');
  const [customAnalyzing, setCustomAnalyzing] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getCompetitorOverview(siteId);
      setOverview(data);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [siteId]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await api.getCompetitorHistory(siteId);
      setHistory(data);
    } catch (e) {
      setError(e.message);
    }
    setHistoryLoading(false);
  }, [siteId]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  // Lazy-load history the first time the tab is opened
  useEffect(() => { if (tab === 'history' && !history && !historyLoading) loadHistory(); }, [tab, history, historyLoading, loadHistory]);

  const handleAnalyze = async (kwId) => {
    setAnalyzing(kwId);
    setError('');
    try {
      const result = await api.runCompetitorAnalysis(kwId);
      setSelectedKw(kwId);
      setDetail(result);
      setGapAnalysis(result.gap_analysis || null);
      setTab('detail');
      loadOverview();
    } catch (e) {
      setError('שגיאה בניתוח: ' + e.message);
    }
    setAnalyzing(null);
  };

  const handleViewDetail = async (kwId) => {
    setSelectedKw(kwId);
    setDetailLoading(true);
    setTab('detail');
    try {
      const data = await api.getCompetitorAnalysis(kwId);
      setDetail(data);
    } catch (e) {
      setError(e.message);
    }
    setDetailLoading(false);
  };

  const handleRunClaude = async (kwId) => {
    setGapLoading(true);
    try {
      const result = await api.runClaudeGapAnalysis(kwId);
      setGapAnalysis(result.gap_analysis);
    } catch (e) {
      setError(e.message);
    }
    setGapLoading(false);
  };

  const handleCustomAnalyze = async () => {
    if (!customKeyword.trim()) return;
    setCustomAnalyzing(true);
    setError('');
    try {
      const result = await api.analyzeCustomKeyword(siteId, customKeyword.trim());
      setDetail(result);
      setGapAnalysis(result.gap_analysis || null);
      setSelectedKw('custom');
      setTab('detail');
    } catch (e) {
      setError('שגיאה בניתוח: ' + e.message);
    }
    setCustomAnalyzing(false);
  };

  if (loading) return <div style={S.page}><div style={S.card}>טוען נתונים...</div></div>;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.title}>ניתוח מתחרים - {overview?.site_name}</div>
        <div style={S.subtitle}>
          {overview?.total_keywords} ביטויים | {overview?.analyzed_keywords} נותחו
        </div>
      </div>

      {error && <div style={S.alert('warning')}>{error}</div>}

      {/* Custom keyword search */}
      <div style={{
        background: '#111827', border: '1px solid #1E293B', borderRadius: 10,
        padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>ניתוח ביטוי:</span>
        <input
          value={customKeyword}
          onChange={e => setCustomKeyword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleCustomAnalyze()}
          placeholder="הקלד ביטוי חופשי לניתוח מתחרים..."
          style={{
            flex: 1, background: '#0B0F1A', border: '1px solid #1E293B', borderRadius: 6,
            padding: '8px 12px', color: '#E2E8F0', fontSize: 13, fontFamily: 'inherit',
          }}
        />
        <button
          onClick={handleCustomAnalyze}
          disabled={customAnalyzing || !customKeyword.trim()}
          style={{
            background: '#3B82F6', color: '#fff', border: 'none', borderRadius: 8,
            padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: customAnalyzing ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit', opacity: customAnalyzing || !customKeyword.trim() ? 0.5 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {customAnalyzing ? 'מנתח...' : 'נתח'}
        </button>
      </div>

      <div style={S.tabs}>
        <button style={S.tab(tab === 'overview')} onClick={() => setTab('overview')}>סקירה כללית</button>
        <button style={S.tab(tab === 'detail')} onClick={() => setTab('detail')} disabled={!selectedKw}>
          ניתוח מפורט {selectedKw ? `(${overview?.keywords?.find(k => k.keyword_id === selectedKw)?.keyword || ''})` : ''}
        </button>
        <button style={S.tab(tab === 'history')} onClick={() => setTab('history')}>היסטוריית מתחרים</button>
      </div>

      {tab === 'overview' && <OverviewTab overview={overview} analyzing={analyzing} onAnalyze={handleAnalyze} onView={handleViewDetail} />}
      {tab === 'detail' && <DetailTab detail={detail} loading={detailLoading} gapAnalysis={gapAnalysis} gapLoading={gapLoading} onRunClaude={handleRunClaude} selectedKw={selectedKw} siteId={siteId} />}
      {tab === 'history' && <HistoryTab history={history} loading={historyLoading} onReload={loadHistory} />}
    </div>
  );
}

// ─── Overview Tab ────────────────────────────

function OverviewTab({ overview, analyzing, onAnalyze, onView }) {
  const kws = overview?.keywords || [];
  const withPos = kws.filter(k => k.our_position);
  const top3 = withPos.filter(k => k.our_position <= 3).length;
  const top10 = withPos.filter(k => k.our_position <= 10).length;
  const striking = withPos.filter(k => k.our_position > 3 && k.our_position <= 20).length;

  return (
    <>
      <div style={S.stat}>
        <div style={S.statBox}>
          <div style={S.statLabel}>Top 3</div>
          <div style={{ ...S.statValue, color: '#22C55E' }}>{top3}</div>
        </div>
        <div style={S.statBox}>
          <div style={S.statLabel}>Top 10</div>
          <div style={{ ...S.statValue, color: '#3B82F6' }}>{top10}</div>
        </div>
        <div style={S.statBox}>
          <div style={S.statLabel}>Striking Distance (4-20)</div>
          <div style={{ ...S.statValue, color: '#F59E0B' }}>{striking}</div>
        </div>
        <div style={S.statBox}>
          <div style={S.statLabel}>נותחו</div>
          <div style={S.statValue}>{overview?.analyzed_keywords || 0}</div>
          <div style={S.progress}>
            <div style={S.progressFill(kws.length ? (overview?.analyzed_keywords / kws.length) * 100 : 0, '#22C55E')} />
          </div>
        </div>
      </div>

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>ביטוי</th>
              <th style={{ ...S.th, textAlign: 'center', width: 70 }}>מיקום</th>
              <th style={{ ...S.th, textAlign: 'center', width: 90 }}>מתחרים</th>
              <th style={{ ...S.th, textAlign: 'center', width: 120 }}>ניתוח אחרון</th>
              <th style={{ ...S.th, textAlign: 'center', width: 140 }}>פעולה</th>
            </tr>
          </thead>
          <tbody>
            {kws.map((kw) => (
              <tr key={kw.keyword_id} style={{ transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={S.td}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{kw.keyword}</div>
                  {kw.our_url && (
                    <div style={{ fontSize: 11, color: '#4B5C73', marginTop: 2, direction: 'ltr', textAlign: 'right' }}>
                      {new URL(kw.our_url).pathname.substring(0, 40)}{new URL(kw.our_url).pathname.length > 40 ? '...' : ''}
                    </div>
                  )}
                </td>
                <td style={{ ...S.td, ...S.pos(kw.our_position) }}>
                  {kw.our_position || '-'}
                </td>
                <td style={{ ...S.td, textAlign: 'center' }}>
                  {kw.has_analysis ? (
                    <span style={{ ...S.badge('info') }}>{kw.competitors_analyzed}</span>
                  ) : (
                    <span style={{ color: '#4B5C73' }}>-</span>
                  )}
                </td>
                <td style={{ ...S.td, textAlign: 'center', fontSize: 12, color: '#64748B' }}>
                  {kw.last_analyzed ? new Date(kw.last_analyzed).toLocaleDateString('he-IL') : '-'}
                </td>
                <td style={{ ...S.td, textAlign: 'center' }}>
                  {kw.has_analysis ? (
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                      <button style={S.btn('secondary')} onClick={() => onView(kw.keyword_id)}>צפה</button>
                      <button
                        style={S.btn('primary', analyzing === kw.keyword_id)}
                        onClick={() => onAnalyze(kw.keyword_id)}
                        disabled={analyzing === kw.keyword_id}
                      >
                        {analyzing === kw.keyword_id ? '...' : 'עדכן'}
                      </button>
                    </div>
                  ) : (
                    <button
                      style={S.btn('primary', analyzing === kw.keyword_id)}
                      onClick={() => onAnalyze(kw.keyword_id)}
                      disabled={analyzing === kw.keyword_id}
                    >
                      {analyzing === kw.keyword_id ? 'מנתח...' : 'נתח מתחרים'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── History Tab ─────────────────────────────

function HistoryTab({ history, loading, onReload }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [onlyBehind, setOnlyBehind] = useState(false);

  if (loading) return <div style={S.card}>טוען היסטוריה...</div>;
  if (!history) return <div style={S.card}>אין נתונים.</div>;

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  let kws = history.keywords || [];
  // "מאחורינו" = keywords where we are not ranked #1 (there is a domain above us)
  if (onlyBehind) kws = kws.filter(k => k.our_position && k.our_position !== 1);
  const domain = (d) => d || '—';

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={S.subtitle}>
          {history.total_keywords} ביטויים | היסטוריית המתחרים המובילים מבדיקות המיקומים
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94A3B8', cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyBehind} onChange={e => setOnlyBehind(e.target.checked)} />
            רק ביטויים שאיננו #1
          </label>
          <button style={S.btn('secondary')} onClick={onReload}>רענן</button>
        </div>
      </div>

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: 30 }}></th>
              <th style={S.th}>ביטוי</th>
              <th style={{ ...S.th, textAlign: 'center', width: 80 }}>מיקום נוכחי</th>
              <th style={S.th}>מתחרה מוביל נוכחי</th>
              <th style={{ ...S.th, textAlign: 'center', width: 80 }}>בדיקות</th>
            </tr>
          </thead>
          <tbody>
            {kws.map((kw) => {
              const open = expanded.has(kw.keyword_id);
              return (
                <React.Fragment key={kw.keyword_id}>
                  <tr style={{ cursor: kw.checks ? 'pointer' : 'default' }} onClick={() => kw.checks && toggle(kw.keyword_id)}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...S.td, textAlign: 'center', color: '#64748B' }}>
                      {kw.checks ? (open ? '▾' : '▸') : ''}
                    </td>
                    <td style={{ ...S.td, fontWeight: 600, fontSize: 14 }}>{kw.keyword}</td>
                    <td style={{ ...S.td, ...S.pos(kw.our_position) }}>{kw.our_position || '-'}</td>
                    <td style={{ ...S.td, direction: 'ltr', textAlign: 'right', color: kw.current_top_competitor ? '#94A3B8' : '#4B5C73' }}>
                      {domain(kw.current_top_competitor)}
                    </td>
                    <td style={{ ...S.td, textAlign: 'center', fontSize: 12, color: '#64748B' }}>{kw.checks || 0}</td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={5} style={{ ...S.td, background: '#0B0F1A', padding: '8px 16px' }}>
                        {kw.history.length === 0 ? (
                          <div style={{ color: '#4B5C73', fontSize: 12 }}>אין היסטוריה.</div>
                        ) : (
                          <table style={{ ...S.table, fontSize: 12 }}>
                            <thead>
                              <tr>
                                <th style={{ ...S.th, width: 120 }}>תאריך</th>
                                <th style={{ ...S.th, textAlign: 'center', width: 70 }}>המיקום שלנו</th>
                                <th style={S.th}>מתחרה מוביל</th>
                                <th style={S.th}>Top 3 מתחרים</th>
                              </tr>
                            </thead>
                            <tbody>
                              {kw.history.map((h, i) => (
                                <tr key={i}>
                                  <td style={{ ...S.td, color: '#94A3B8' }}>
                                    {h.date ? new Date(h.date).toLocaleDateString('he-IL') : '-'}
                                  </td>
                                  <td style={{ ...S.td, ...S.pos(h.our_position) }}>{h.our_position || '-'}</td>
                                  <td style={{ ...S.td, direction: 'ltr', textAlign: 'right', color: h.top_competitor ? '#E2E8F0' : '#4B5C73' }}>
                                    {domain(h.top_competitor)}
                                  </td>
                                  <td style={{ ...S.td, direction: 'ltr', textAlign: 'right', color: '#64748B', fontSize: 11 }}>
                                    {(h.top3 || []).map(c => c.position + '. ' + c.domain).join('  |  ') || '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Detail Tab ──────────────────────────────

function DetailTab({ detail, loading, gapAnalysis, gapLoading, onRunClaude, selectedKw, siteId }) {
  if (loading) return <div style={S.card}>טוען ניתוח...</div>;
  if (!detail) return <div style={S.card}>בחר ביטוי מטבלת הסקירה.</div>;

  const competitors = detail.competitors || [];
  const ourScan = detail.our_scan;

  return (
    <>
      {/* Summary stats */}
      <div style={S.stat}>
        <div style={S.statBox}>
          <div style={S.statLabel}>המיקום שלנו</div>
          <div style={{ ...S.statValue, color: detail.our_position ? (detail.our_position <= 3 ? '#22C55E' : detail.our_position <= 10 ? '#3B82F6' : '#F59E0B') : '#EF4444' }}>
            {detail.our_position || 'לא מדורג'}
          </div>
        </div>
        <div style={S.statBox}>
          <div style={S.statLabel}>מתחרים שנותחו</div>
          <div style={S.statValue}>{competitors.length}</div>
        </div>
        <div style={S.statBox}>
          <div style={S.statLabel}>SERP Features</div>
          <div style={{ fontSize: 13, color: '#94A3B8' }}>
            {(detail.serp_features || []).join(', ') || 'אין'}
          </div>
        </div>
      </div>

      {/* Comparison table */}
      <div style={S.card}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>השוואת עמודים</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>עמוד</th>
                <th style={{ ...S.th, textAlign: 'center' }}>מיקום</th>
                <th style={{ ...S.th, textAlign: 'center' }}>מילים</th>
                <th style={{ ...S.th, textAlign: 'center' }}>H2</th>
                <th style={{ ...S.th, textAlign: 'center' }}>H3</th>
                <th style={{ ...S.th, textAlign: 'center' }}>תמונות</th>
                <th style={{ ...S.th, textAlign: 'center' }}>Schema</th>
                <th style={{ ...S.th, textAlign: 'center' }}>DR</th>
                <th style={{ ...S.th, textAlign: 'center' }}>Backlinks</th>
              </tr>
            </thead>
            <tbody>
              {/* Our page */}
              {ourScan && (
                <tr style={{ background: 'rgba(59,130,246,0.06)' }}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 700, color: '#3B82F6', fontSize: 13 }}>אנחנו</div>
                    <div style={{ fontSize: 11, color: '#64748B', direction: 'ltr', textAlign: 'right' }}>
                      {ourScan.domain}
                    </div>
                  </td>
                  <td style={{ ...S.td, ...S.pos(detail.our_position) }}>{detail.our_position || '-'}</td>
                  <td style={{ ...S.td, textAlign: 'center', fontFamily: "'JetBrains Mono'" }}>{ourScan.word_count || '-'}</td>
                  <td style={{ ...S.td, textAlign: 'center' }}>{ourScan.h2_count || 0}</td>
                  <td style={{ ...S.td, textAlign: 'center' }}>{ourScan.h3_count || 0}</td>
                  <td style={{ ...S.td, textAlign: 'center' }}>{ourScan.images_count || 0}</td>
                  <td style={{ ...S.td, textAlign: 'center', fontSize: 11 }}>{(ourScan.schema_types || []).join(', ') || '-'}</td>
                  <td style={{ ...S.td, textAlign: 'center' }}>
                    {detail.ahrefs_data?.[ourScan.domain?.replace('www.', '')]?.domain_rating || '-'}
                  </td>
                  <td style={{ ...S.td, textAlign: 'center' }}>
                    {detail.ahrefs_data?.[ourScan.domain?.replace('www.', '')]?.backlinks_count || '-'}
                  </td>
                </tr>
              )}
              {/* Competitors */}
              {competitors.map((c, i) => {
                const ah = detail.ahrefs_data?.[c.domain?.replace('www.', '')] || {};
                const ca = c.content_analysis || c;
                return (
                  <tr key={i} style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={S.td}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{c.domain}</div>
                      <a href={c.url || c.competitor_url} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 11, color: '#4B5C73', textDecoration: 'none' }}>
                        {(c.url || c.competitor_url || '').substring(0, 50)}...
                      </a>
                    </td>
                    <td style={{ ...S.td, ...S.pos(c.position) }}>{c.position || '-'}</td>
                    <td style={{ ...S.td, textAlign: 'center', fontFamily: "'JetBrains Mono'" }}>{c.word_count || ca.word_count || '-'}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}>{c.h2_count ?? ca.h2_count ?? '-'}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}>{c.h3_count ?? ca.h3_count ?? '-'}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}>{c.images_count ?? ca.images_count ?? '-'}</td>
                    <td style={{ ...S.td, textAlign: 'center', fontSize: 11 }}>{(c.schema_types || []).join(', ') || '-'}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}>{c.domain_authority ?? ah.domain_rating ?? '-'}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}>{c.backlinks_count ?? ah.backlinks_count ?? '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Visual gaps */}
      {ourScan && competitors.length > 0 && (
        <div style={S.card}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>פערים ויזואליים</div>
          <GapBars ourScan={ourScan} competitors={competitors} ahrefs={detail.ahrefs_data} />
        </div>
      )}

      {/* Claude Analysis */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>ניתוח Claude - המלצות</div>
          <button
            style={S.btn('primary', gapLoading)}
            onClick={() => onRunClaude(selectedKw)}
            disabled={gapLoading}
          >
            {gapLoading ? 'מנתח...' : gapAnalysis ? 'נתח מחדש' : 'הפעל ניתוח Claude'}
          </button>
        </div>

        {gapAnalysis ? (
          <ClaudeAnalysis analysis={gapAnalysis} siteId={siteId} keyword={detail?.keyword} targetUrl={detail?.our_url} />
        ) : (
          <div style={{ color: '#64748B', fontSize: 14 }}>
            לחץ "הפעל ניתוח Claude" כדי לקבל המלצות מבוססות AI לשיפור המיקום.
          </div>
        )}
      </div>
    </>
  );
}

// ─── Gap Bars Component ──────────────────────

function GapBars({ ourScan, competitors, ahrefs }) {
  const avgComp = (field) => {
    const vals = competitors.map(c => {
      const ca = c.content_analysis || c;
      return ca[field] ?? c[field];
    }).filter(v => v != null && v > 0);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
  };

  const metrics = [
    { label: 'מילים', ours: ourScan.word_count || 0, theirs: avgComp('word_count') },
    { label: 'כותרות H2', ours: ourScan.h2_count || 0, theirs: avgComp('h2_count') },
    { label: 'כותרות H3', ours: ourScan.h3_count || 0, theirs: avgComp('h3_count') },
    { label: 'תמונות', ours: ourScan.images_count || 0, theirs: avgComp('images_count') },
  ];

  return (
    <div>
      {metrics.map((m, i) => {
        const max = Math.max(m.ours, m.theirs, 1);
        const ourPct = (m.ours / max) * 100;
        const theirPct = (m.theirs / max) * 100;
        const ahead = m.ours >= m.theirs;

        return (
          <div key={i} style={{ padding: '10px 0', borderBottom: i < metrics.length - 1 ? '1px solid rgba(30,41,59,0.3)' : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
              <span style={{ color: '#94A3B8' }}>{m.label}</span>
              <span style={{ fontFamily: "'JetBrains Mono'", fontSize: 12 }}>
                <span style={{ color: '#3B82F6' }}>אנחנו: {m.ours.toLocaleString()}</span>
                <span style={{ color: '#4B5C73', margin: '0 8px' }}>|</span>
                <span style={{ color: '#F59E0B' }}>ממוצע מתחרים: {m.theirs.toLocaleString()}</span>
              </span>
            </div>
            <div style={{ display: 'flex', gap: 4, height: 12 }}>
              <div style={{ flex: 1, background: '#1E293B', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: ourPct + '%', background: ahead ? '#22C55E' : '#3B82F6', borderRadius: 4, transition: 'width 0.5s' }} />
              </div>
              <div style={{ flex: 1, background: '#1E293B', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: theirPct + '%', background: '#F59E0B', borderRadius: 4, transition: 'width 0.5s' }} />
              </div>
            </div>
            {!ahead && (
              <div style={{ fontSize: 11, color: '#EF4444', marginTop: 4 }}>
                חסרים {(m.theirs - m.ours).toLocaleString()} {m.label}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Claude Analysis Component ───────────────

function ClaudeAnalysis({ analysis, siteId, keyword, targetUrl }) {
  const [adding, setAdding] = React.useState({});
  const [added, setAdded] = React.useState({});

  const addToChecklist = async (description, priority, type) => {
    const key = description.substring(0, 30);
    setAdding(prev => ({ ...prev, [key]: true }));
    try {
      const api = (await import('../api')).default;
      await api.createTask({
        site_id: parseInt(siteId),
        type: type || 'seo_improvement',
        description: description,
        priority: priority || 'medium',
        source: 'competitor',
        source_keyword: keyword || '',
        target_url: targetUrl || '',
      });
      setAdded(prev => ({ ...prev, [key]: true }));
    } catch (e) {
      console.error('Failed to add task:', e);
    }
    setAdding(prev => ({ ...prev, [key]: false }));
  };

  const AddBtn = ({ desc, priority, type }) => {
    const key = desc.substring(0, 30);
    if (added[key]) return <span style={{ fontSize: 11, color: '#22C55E' }}>✓ נוסף</span>;
    return (
      <button
        style={{
          background: 'none', border: '1px solid #2D3A4F', borderRadius: 6,
          color: '#94A3B8', fontSize: 11, padding: '3px 8px', cursor: 'pointer',
          fontFamily: 'inherit', transition: 'all 0.2s', whiteSpace: 'nowrap',
        }}
        onClick={() => addToChecklist(desc, priority, type)}
        disabled={adding[key]}
        onMouseEnter={e => { e.target.style.borderColor = '#3B82F6'; e.target.style.color = '#3B82F6'; }}
        onMouseLeave={e => { e.target.style.borderColor = '#2D3A4F'; e.target.style.color = '#94A3B8'; }}
      >
        {adding[key] ? '...' : '+ Checklist'}
      </button>
    );
  };

  const AddAllBtn = () => {
    const [allAdding, setAllAdding] = React.useState(false);
    const [allDone, setAllDone] = React.useState(false);
    
    const addAll = async () => {
      setAllAdding(true);
      try {
        const api = (await import('../api')).default;
        const tasks = [];
        (analysis.quick_wins || []).forEach(w => {
          tasks.push({
            site_id: parseInt(siteId),
            type: 'seo_improvement',
            description: w,
            priority: 'high',
            source: 'competitor',
            source_keyword: keyword || '',
            target_url: targetUrl || '',
          });
        });
        (analysis.priority_actions || []).forEach(a => {
          tasks.push({
            site_id: parseInt(siteId),
            type: 'seo_improvement',
            description: a.action,
            priority: a.impact || 'medium',
            source: 'competitor',
            source_keyword: keyword || '',
            target_url: targetUrl || '',
          });
        });
        if (tasks.length > 0) {
          await api.createTasksBulk(tasks);
          const newAdded = {};
          tasks.forEach(t => { newAdded[t.description.substring(0, 30)] = true; });
          setAdded(prev => ({ ...prev, ...newAdded }));
          setAllDone(true);
        }
      } catch (e) {
        console.error('Failed to add all tasks:', e);
      }
      setAllAdding(false);
    };

    if (allDone) return <span style={{ fontSize: 12, color: '#22C55E', fontWeight: 600 }}>✓ כל ההמלצות נוספו ל-Checklist</span>;
    return (
      <button
        style={{
          background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
          borderRadius: 8, color: '#22C55E', fontSize: 13, padding: '8px 16px',
          cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
        }}
        onClick={addAll}
        disabled={allAdding}
      >
        {allAdding ? 'מוסיף...' : 'הוסף הכל ל-Checklist'}
      </button>
    );
  };

  if (analysis.parse_error) {
    return <div style={S.alert('warning')}>שגיאה בניתוח: {analysis.parse_error}</div>;
  }

  return (
    <div>
      {/* Summary */}
      {analysis.summary && (
        <div style={{ ...S.alert('info'), marginBottom: 16 }}>
          {analysis.summary}
        </div>
      )}

      {/* Add All button */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <AddAllBtn />
      </div>

      {/* Quick Wins */}
      {analysis.quick_wins?.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: '#22C55E' }}>פעולות מהירות</div>
          {analysis.quick_wins.map((w, i) => (
            <div key={i} style={{ padding: '6px 0', fontSize: 13, color: '#E2E8F0', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                <span style={{ color: '#22C55E' }}>✓</span> {w}
              </div>
              <AddBtn desc={w} priority="high" type="quick_win" />
            </div>
          ))}
        </div>
      )}

      {/* Priority Actions */}
      {analysis.priority_actions?.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>פעולות לפי עדיפות</div>
          {analysis.priority_actions.map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(30,41,59,0.3)' }}>
              <span style={S.badge(a.impact)}>{a.impact === 'high' ? 'גבוה' : a.impact === 'medium' ? 'בינוני' : 'נמוך'}</span>
              <span style={{ fontSize: 13, flex: 1 }}>{a.action}</span>
              <span style={{ fontSize: 11, color: '#64748B', whiteSpace: 'nowrap' }}>
                מאמץ: {a.effort === 'high' ? 'רב' : a.effort === 'medium' ? 'בינוני' : 'מועט'}
              </span>
              <AddBtn desc={a.action} priority={a.impact} type="seo_improvement" />
            </div>
          ))}
        </div>
      )}

      {/* Gap Details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {analysis.content_gap && (
          <div style={{ background: '#1A2237', borderRadius: 8, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>פער תוכן</div>
              {analysis.content_gap.recommendation && <AddBtn desc={analysis.content_gap.recommendation} priority="high" type="content_gap" />}
            </div>
            <div style={{ fontSize: 12, color: '#94A3B8' }}>
              אנחנו: {analysis.content_gap.our_words || '?'} מילים<br />
              ממוצע מתחרים: {analysis.content_gap.avg_competitor_words || '?'} מילים
            </div>
            <div style={{ fontSize: 12, color: '#E2E8F0', marginTop: 6 }}>{analysis.content_gap.recommendation}</div>
          </div>
        )}
        {analysis.schema_gap && (
          <div style={{ background: '#1A2237', borderRadius: 8, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>פער Schema</div>
              {analysis.schema_gap.recommendation && <AddBtn desc={analysis.schema_gap.recommendation} priority="high" type="schema" />}
            </div>
            <div style={{ fontSize: 12, color: '#E2E8F0' }}>{analysis.schema_gap.recommendation}</div>
          </div>
        )}
        {analysis.backlink_gap && (
          <div style={{ background: '#1A2237', borderRadius: 8, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>פער Backlinks</div>
              {analysis.backlink_gap.recommendation && <AddBtn desc={analysis.backlink_gap.recommendation} priority="medium" type="backlinks" />}
            </div>
            <div style={{ fontSize: 12, color: '#94A3B8' }}>
              DR שלנו: {analysis.backlink_gap.our_dr || '?'}<br />
              DR ממוצע מתחרים: {analysis.backlink_gap.avg_competitor_dr || '?'}
            </div>
            <div style={{ fontSize: 12, color: '#E2E8F0', marginTop: 6 }}>{analysis.backlink_gap.recommendation}</div>
          </div>
        )}
        {analysis.structure_gap && (
          <div style={{ background: '#1A2237', borderRadius: 8, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>פער מבנה</div>
              {analysis.structure_gap.recommendation && <AddBtn desc={analysis.structure_gap.recommendation} priority="medium" type="structure" />}
            </div>
            <div style={{ fontSize: 12, color: '#E2E8F0' }}>{analysis.structure_gap.recommendation}</div>
          </div>
        )}
      </div>

      {/* Estimated improvement */}
      {analysis.estimated_improvement && (
        <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(34,197,94,0.08)', borderRadius: 8, fontSize: 13, color: '#22C55E' }}>
          הערכת שיפור: {analysis.estimated_improvement}
        </div>
      )}

      {analysis.analysis_cost && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#4B5C73', textAlign: 'left', direction: 'ltr' }}>
          Analysis cost: ${analysis.analysis_cost?.toFixed(4)}
        </div>
      )}
    </div>
  );
}
